import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Lightbulb, Target, Heart, Users, Sparkles, Trophy, Globe, Shield, Layers, Scale, Eye } from 'lucide-react';
import AppFooter from '../components/AppFooter';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              className="gap-2"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            <div className="text-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                About Idea Fountain
              </h1>
              <p className="text-xs text-slate-600 italic mt-1">
                Human Creativity. AI Partnership. Responsible Innovation.
              </p>
            </div>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-5xl">
        {/* Hero Section - Humanity-in-the-Loop */}
        <div className="mb-16">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full shadow-xl">
              <span className="text-4xl">🪶</span>
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center leading-tight">
            Humanity-in-the-Loop: The Next Frontier
          </h2>
          <div className="space-y-6 max-w-4xl mx-auto">
            <p className="text-lg text-slate-700 leading-relaxed text-justify">
              IdeaFountain moves beyond Human-in-the-Loop (individual oversight) to <strong>Humanity-in-the-Loop</strong> — embedding collective ethics, social empathy, and inclusive innovation into the core of its design.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed text-justify">
              By measuring ethical groundedness, applying IDEO's human-centered design, and aligning creativity with the UN SDGs, the platform transforms responsible innovation from a technical safeguard into a moral and societal practice.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed text-justify">
              It keeps not just humans, but <strong>humanity</strong>, in the equation.
            </p>
          </div>
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-l-4 border-purple-600 rounded-lg p-6 max-w-3xl mx-auto mt-8">
            <p className="text-slate-800 italic text-lg leading-relaxed text-left">
              "We are not teaching technology to think — we are teaching humanity to think with technology, consciously."
            </p>
            <p className="text-purple-700 font-semibold mt-3 text-left">— Priyanka Jha, Founder - Idea Fountain</p>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <Card className="border-2 border-purple-200">
            <CardHeader>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-3">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-2xl">Vision</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4 font-semibold">
                To place humanity back in the loop of innovation.
              </p>
              <p className="text-slate-600 leading-relaxed">
                IdeaFountain.Studio envisions a future where artificial intelligence becomes an amplifier of human wisdom — not a replacement for it.
              </p>
              <p className="text-slate-600 leading-relaxed mt-3">
                By transforming Human-in-the-Loop into <strong>Humanity-in-the-Loop</strong>, we aim to guide the next evolution of responsible innovation — where creativity, ethics, and empathy co-create progress that serves people, planet, and purpose.
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-pink-200">
            <CardHeader>
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center mb-3">
                <Target className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-2xl">Mission</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4 font-semibold">
                To help people think with AI — and to help AI think with humanity.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our mission is to empower innovators, researchers, and creators to harness AI as a partner in reflection, not merely a generator of output.
              </p>
              <p className="text-slate-600 leading-relaxed mt-3">
                Through structured frameworks — the Originality Enabling System (OES), Originality Index (OI), Mirror-Spark Assessment (MSA), and Interaction Quality Index (IQI) — we measure and cultivate ideas that are not only novel but also ethically grounded, socially resonant, and globally coherent.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center text-slate-900 mb-3">🌍 Core Values</h3>
          <p className="text-center text-slate-600 mb-10 max-w-3xl mx-auto">
            Five principles that guide everything we create and measure
          </p>
          <div className="space-y-6">
            <Card className="border-2 border-l-4 border-l-purple-600 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-slate-900 mb-2">1. Humanity-in-the-Loop</h4>
                    <p className="text-slate-600 leading-relaxed">
                      We expand beyond individual oversight to embed collective human ethics, empathy, and inclusivity into every layer of innovation. Technology should evolve <strong>with humanity</strong> — not apart from it.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-l-4 border-l-blue-600 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-slate-900 mb-2">2. Ethical Responsibility</h4>
                    <p className="text-slate-600 leading-relaxed">
                      Every spark of creativity carries moral consequence. We ensure that innovation contributes to human flourishing, equity, and planetary well-being.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-l-4 border-l-emerald-600 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Layers className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-slate-900 mb-2">3. Structured Creativity</h4>
                    <p className="text-slate-600 leading-relaxed">
                      Originality needs architecture. The OES provides a repeatable pathway from idea to impact — turning inspiration into an accountable, measurable process.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-l-4 border-l-pink-600 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-slate-900 mb-2">4. Responsible Innovation</h4>
                    <p className="text-slate-600 leading-relaxed">
                      The IQI embeds fairness, inclusivity, and accountability into the act of creation itself — measuring not just what is built, but <strong>how</strong> it's built.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-l-4 border-l-orange-600 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Scale className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-slate-900 mb-2">5. Transparency and Trust</h4>
                    <p className="text-slate-600 leading-relaxed">
                      Innovation earns legitimacy through clarity. Our multi-dimensional scorecard makes originality explainable — transforming creativity from a mystery into a shared, ethical science.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Global Alignment */}
        <Card className="mb-16 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardHeader>
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mb-3 mx-auto">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl text-center">🌎 Global Alignment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-700 text-center leading-relaxed font-medium">
              Aligned with global frameworks for responsible technology:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-5 border border-blue-200">
                <h4 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                  <span className="text-2xl">🇺🇳</span> UN SDGs
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Aligned with Goals 4, 9, 12, and 16 — advancing education, innovation, and justice through ethical metrics.
                </p>
              </div>

              <div className="bg-white rounded-lg p-5 border border-blue-200">
                <h4 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                  <span className="text-2xl">🏛️</span> WEF Principles
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  World Economic Forum's Principles for Responsible AI — transparency, inclusivity, and human-centric design.
                </p>
              </div>

              <div className="bg-white rounded-lg p-5 border border-blue-200">
                <h4 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                  <span className="text-2xl">📜</span> OECD / UNESCO
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Guidelines for fairness, accountability, and human rights in AI systems.
                </p>
              </div>

              <div className="bg-white rounded-lg p-5 border border-blue-200">
                <h4 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                  <span className="text-2xl">🌍</span> National Frameworks
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Complementing the EU AI Act, UK CDEI, and India's Responsible AI Strategy.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white mt-6">
              <p className="text-center text-lg italic leading-relaxed">
                "IdeaFountain.Studio acts as a <strong>coherence engine</strong> for responsible innovation — translating global ethical principles into measurable creative intelligence."
              </p>
            </div>
          </CardContent>
        </Card>

        {/* The Story */}
        <Card className="mb-16 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="text-3xl text-center">The Story</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Idea Fountain was born from a profound understanding: <strong>thinking with AI is the way forward</strong>.
            </p>
            <p>
              Traditional approaches to creativity struggled with subjectivity, bias, and lack of structure. 
              We recognized that artificial intelligence—when partnered with human consciousness—could 
              fundamentally transform how we think, create, and innovate.
            </p>
            <p>
              Founded by <strong>Priyanka Jha</strong>, IF™ emerged as a pioneering system that shows how 
              creative originality can be <strong>augmented with AI and technology</strong>. We don't just 
              generate ideas—we enable them as a <strong>structured thought process</strong>, make them 
              <strong>measurable</strong> through rigorous frameworks (OI, MSA, IQI), and ground everything 
              in the <strong>ethics and consciousness of humanity</strong>.
            </p>
            <p>
              Inspired by Ayn Rand's "The Fountainhead," we believe every person is a fountainhead of original
              ideas. Our mission is to help you harness AI as a partner in reflection—tapping into your potential,
              measuring your progress, and connecting with innovators globally—all while keeping humanity at the center.
            </p>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card className="mb-16 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
          <CardContent className="pt-6 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-3">Get in Touch</h3>
            <p className="text-slate-700 mb-4">
              Have questions, feedback, or want to collaborate? We'd love to hear from you.
            </p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-slate-600">You can reach us at:</span>
              <a 
                href="mailto:hey@ideafountain.studio"
                className="font-semibold text-purple-600 hover:text-purple-700 transition-colors inline-flex items-center gap-2"
              >
                <span className="text-xl">✉️</span>
                hey@ideafountain.studio
              </a>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-4">Join the Originality Revolution</h3>
          <p className="text-xl mb-8 text-white/90">
            Be part of a global community unlocking human creative potential
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-purple-600 hover:bg-white/90"
              onClick={() => navigate('/auth')}
            >
              Get Started Free
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/challenges')}
            >
              Explore Challenges
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <AppFooter className="bg-gradient-to-r from-purple-100 to-pink-100 border-t-slate-300" />
    </div>
  );
};

export default AboutUs;
